import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

interface QrScannerModalProps {
  open: boolean;
  titulo?: string;
  scanError?: string | null;
  onClose: () => void;
  onScan: (decodedText: string) => void;
  onClearScanError?: () => void;
}

const SCANNER_ELEMENT_ID = 'nexus-qr-reader';

const QrScannerModal: React.FC<QrScannerModalProps> = ({
  open,
  titulo = 'Ler QR Code da etiqueta',
  scanError = null,
  onClose,
  onScan,
  onClearScanError,
}) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const onScanRef = useRef(onScan);
  const handledRef = useRef(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  onScanRef.current = onScan;

  useEffect(() => {
    if (!open) return;

    handledRef.current = false;
    setCameraError(null);
    setStarting(true);

    let cancelled = false;

    const startScanner = async () => {
      if (!window.isSecureContext) {
        setStarting(false);
        setCameraError(
          'A câmera só funciona com conexão segura (HTTPS). Acesse o app pelo endereço https.'
        );
        return;
      }

      try {
        const existing = document.getElementById(SCANNER_ELEMENT_ID);
        if (!existing) {
          setStarting(false);
          setCameraError('Área do scanner indisponível. Tente novamente.');
          return;
        }

        const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, {
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          verbose: false,
        });
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1,
          },
          (decodedText) => {
            if (handledRef.current || cancelled) return;
            handledRef.current = true;
            onScanRef.current(decodedText.trim());
          },
          () => undefined
        );

        if (!cancelled) setStarting(false);
      } catch {
        if (!cancelled) {
          setStarting(false);
          setCameraError(
            'Não foi possível acessar a câmera. Verifique a permissão e tente novamente.'
          );
        }
      }
    };

    const timer = window.setTimeout(() => {
      void startScanner();
    }, 120);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      const scanner = scannerRef.current;
      scannerRef.current = null;
      if (scanner?.isScanning) {
        void scanner.stop().catch(() => undefined);
      } else if (scanner) {
        try {
          scanner.clear();
        } catch {
          /* ignore */
        }
      }
    };
  }, [open, retryKey]);

  // Após erro de QR inválido, libera nova leitura sem reiniciar a câmera
  useEffect(() => {
    if (scanError) {
      handledRef.current = false;
    }
  }, [scanError]);

  if (!open) return null;

  const handleRetryCamera = () => {
    setCameraError(null);
    onClearScanError?.();
    setRetryKey((k) => k + 1);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="qr-scanner-title"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
          <h2 id="qr-scanner-title" className="text-base font-semibold text-gray-900">
            {titulo}
          </h2>
          <button
            type="button"
            className="btn-secondary btn-icon text-sm px-3 py-1"
            onClick={onClose}
            aria-label="Fechar scanner"
          >
            Fechar
          </button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-600">
            Aponte a câmera para o QR Code impresso na etiqueta da Nota de Contratação.
          </p>

          <div
            id={SCANNER_ELEMENT_ID}
            className="overflow-hidden rounded-lg bg-black min-h-[260px]"
          />

          {starting && !cameraError && (
            <p className="text-sm text-gray-500 text-center">Abrindo câmera...</p>
          )}

          {scanError && (
            <div className="error-box text-sm mb-0">
              <p>{scanError}</p>
              <p className="mt-1 text-xs text-red-700">Posicione novamente o QR Code para tentar de novo.</p>
            </div>
          )}

          {cameraError && (
            <div className="error-box text-sm mb-0 space-y-2">
              <p>{cameraError}</p>
              <button
                type="button"
                className="btn-primary btn-icon w-full"
                onClick={handleRetryCamera}
              >
                Tentar novamente
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QrScannerModal;
